import React, { useState, useEffect } from 'react';
import {ChevronLeft, ChevronRight, Repeat2, Plus, Edit, Trash2} from 'lucide-react';
import { useAvailabilityViewModel } from '../../view-model/professional/availability-view-model';
import { observer } from 'mobx-react-lite';
import TimeSlotEditModal from './timeslot-edit-modal';
import ConfirmDialog from "../ui/confirm-dialog";
import { formatDateInTimeZone } from '../../utils/timezone';
import { useTranslation } from 'react-i18next';

interface CalendarEvent {
  id: string;
  title: string;
  start: string;
  end: string;
  isRecurring: boolean;
  hasBookings?: boolean;
  date?: string;
  dayOfWeek?: number;
  startDate?: string;
  endDate?: string;
}

const AvailabilityCalendar: React.FC = observer(() => {
  const { t } = useTranslation();
  const viewModel = useAvailabilityViewModel();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);

  // Obtenir le nom du mois à partir de la date courante en utilisant formatDateInTimeZone
  const currentMonthName = formatDateInTimeZone(currentDate, 'MMMM');
  const currentYear = formatDateInTimeZone(currentDate, 'yyyy');
  
  // Utiliser les noms de jours courts pour l'en-tête du calendrier
  const dayNames = [t('availability.dayNames.lun', 'Lun'), t('availability.dayNames.mar', 'Mar'), t('availability.dayNames.mer', 'Mer'), t('availability.dayNames.jeu', 'Jeu'), t('availability.dayNames.ven', 'Ven'), t('availability.dayNames.sam', 'Sam'), t('availability.dayNames.dim', 'Dim')];
  
  // Navigation entre les mois
  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(prev.getMonth() - 1);
      } else {
        newDate.setMonth(prev.getMonth() + 1);
      }
      return newDate;
    });
  };
  
  // Générer les jours du mois en utilisant les fonctions du ViewModel
  const generateCalendarDays = () => {
    const days = [];
    
    // Premier jour du mois
    const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    
    // Dernier jour du mois
    const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    
    // Ajuster pour que lundi soit le premier jour
    const firstDayOfWeek = firstDayOfMonth.getDay() === 0 ? 6 : firstDayOfMonth.getDay() - 1;
    
    // Ajouter les jours du mois précédent pour compléter la première semaine
    const prevMonthLastDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0).getDate();
    
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, prevMonthLastDay - i);
      days.push({
        date,
        isCurrentMonth: false
      });
    }
    
    // Ajouter les jours du mois actuel
    for (let i = 1; i <= lastDayOfMonth.getDate(); i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), i);
      days.push({
        date,
        isCurrentMonth: true
      });
    }
    
    // Ajouter les jours du mois suivant pour compléter la dernière semaine
    const daysNeeded = 42 - days.length; // 6 semaines * 7 jours = 42
    
    for (let i = 1; i <= daysNeeded; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, i);
      days.push({
        date,
        isCurrentMonth: false
      });
    }
    
    return days;
  };
  
  // Convertir les créneaux en événements de calendrier
  useEffect(() => {
    // Créer un nouvel array pour les événements
    const events: CalendarEvent[] = [];
    
    // Vider les événements précédents pour éviter les duplications
    setCalendarEvents([]);
    
    if (viewModel.getAvailability && viewModel.getAvailability.length > 0) {
      // Traiter tous les créneaux disponibles
      viewModel.getAvailability.forEach(slot => {
        // Pour tous les types de créneaux, utiliser leur date exacte
        if (!slot.startDate) return;
        
        // Normaliser la date pour éviter les problèmes de format
        let formattedDate = slot.startDate;
        
        // S'assurer que la date est au format YYYY-MM-DD
        try {
          const dateObj = new Date(slot.startDate);
          formattedDate = formatDateInTimeZone(dateObj, 'yyyy-MM-dd');
        } catch (error) {
          console.error("Erreur lors du formatage de la date:", error);
          return;
        }
        
        // Ajouter le créneau avec sa date exacte
        events.push({
          id: slot.id,
          title: `${slot.startTime} - ${slot.endTime}`,
          start: slot.startTime,
          end: slot.endTime,
          // Utiliser un drapeau pour indiquer si c'est récurrent (pour l'affichage de l'icône)
          isRecurring: slot.type === 'recurring',
          hasBookings: slot.hasBookings || false,
          date: formattedDate
        });
      });
    }
    
    // Créer un tableau d'événements uniques en filtrant par ID
    const uniqueEvents = events.filter((event, index, self) => 
      index === self.findIndex(e => e.id === event.id)
    );
    
    // Mettre à jour les événements du calendrier
    setCalendarEvents(uniqueEvents);
  }, [viewModel.getAvailability]);
  
  // Vérifier si un jour a des événements
  const getEventsForDay = (date: Date) => {
    // Filtrer les événements pour cette date exacte (aussi bien récurrents que ponctuels)
    const dayEvents = calendarEvents.filter(event => {
      if (event.date) {
        try {
          // Normaliser les formats de date pour la comparaison
          const eventDate = new Date(event.date);
          eventDate.setHours(0, 0, 0, 0);
          
          const currentDate = new Date(date);
          currentDate.setHours(0, 0, 0, 0);
          
          // Comparer les dates en format ISO pour éviter les problèmes de fuseau horaire
          const eventDateStr = formatDateInTimeZone(eventDate, 'yyyy-MM-dd');
          const currentDateStr = formatDateInTimeZone(currentDate, 'yyyy-MM-dd');
          
          return eventDateStr === currentDateStr;
        } catch (error) {
          console.error("Erreur lors de la comparaison des dates:", error);
          return false;
        }
      }
      return false;
    });
    
    const uniqueEventsMap = new Map();
    
    dayEvents.forEach(event => {
      const key = `${event.title}-${event.isRecurring ? 'recurring' : 'oneOff'}`;
      if (!uniqueEventsMap.has(key)) {
        uniqueEventsMap.set(key, event);
      }
    });
    
    return Array.from(uniqueEventsMap.values());
  };
  
  // Générer les jours du calendrier
  const days = generateCalendarDays();


  // Gérer la sauvegarde d'un créneau modifié (pour l'instant, juste fermer le modal)
  const handleSaveTimeSlot = (updatedTimeSlot: any) => {
    console.log('Créneau modifié:', updatedTimeSlot);
    // Pour l'instant, nous ne faisons rien avec les données mises à jour
    // La fonctionnalité de mise à jour sera implémentée ultérieurement
  };

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* En-tête du calendrier avec légende */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between p-4 availability-navigate">
          <button
            type="button"
            onClick={() => navigateMonth('prev')}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          </button>
          
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
            {currentMonthName} {currentYear}
          </h2>
          
          <button
            type="button"
            onClick={() => navigateMonth('next')}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
          >
            <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          </button>
        </div>
        
        {/* Légende déplacée en haut */}
        <div className="px-4 py-2 flex justify-center items-center gap-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-secondary"></div>
            <span className="text-xs text-gray-600 dark:text-gray-400">{t('availability.oneTimeSlots', 'Créneau ponctuel')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-primary"></div>
            <span className="text-xs text-gray-600 dark:text-gray-400">{t('availability.recurringSlots', 'Créneau récurrent')}</span>
          </div>
        </div>
      </div>
      
      {/* Jours de la semaine */}
      <div className="grid grid-cols-7 border-b border-gray-200 dark:border-gray-700">
        {dayNames.map((day: string) => (
          <div key={day} className="py-2 text-center text-sm font-medium text-gray-500 dark:text-gray-400">
            {day}
          </div>
        ))}
      </div>
      
      {/* Grille des jours */}
      <div className="flex-1 grid grid-cols-7 auto-rows-fr overflow-y-auto">
        {days.map(({ date, isCurrentMonth }: {date: Date, isCurrentMonth: boolean}, index: number) => {
          const dayEvents = getEventsForDay(date);
          const isToday = date.toDateString() === new Date().toDateString();
          
          return (
            <div
              key={index}
              className={`
                p-1 border-b border-r border-gray-200 dark:border-gray-700
                ${!isCurrentMonth ? 'bg-gray-50 dark:bg-gray-900/30 text-gray-400 dark:text-gray-600' : ''}
                ${isToday ? 'bg-blue-50 dark:bg-blue-900/10' : ''}
                relative flex flex-col h-full availability-view
              `}
            >
              <div className="flex justify-between items-start">
                <span className={`
                  inline-flex items-center justify-center w-6 h-6 text-sm rounded-full
                  ${isToday ? 'bg-primary-500 text-white' : 'text-gray-700 dark:text-gray-300'}
                `}>
                  {date.getDate()}
                </span>
                
                <div className="flex items-center space-x-1">
                  {dayEvents.length > 0 && (
                    <span className="inline-flex items-center justify-center w-5 h-5 text-xs bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full">
                      {dayEvents.length}
                    </span>
                  )}
                </div>
              </div>
              
              {/* Liste des événements du jour */}
              <div className="mt-1 space-y-1 flex-1 overflow-y-auto h-full scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
                {/* Utiliser un Set pour regrouper les événements par titre et type */}
                {Array.from(new Map(dayEvents.map(event => [
                  // Clé unique basée sur le titre et le type
                  `${event.title}-${event.isRecurring ? 'recurring' : 'oneOff'}`, 
                  event
                ])).values()).map(event => (
                    <div
                        key={event.id}
                        onClick={() => viewModel.setIsEditModalOpen(event)}
                        className={`
                      text-xs p-1 rounded flex justify-between items-center
                      ${event.isRecurring
                            ? 'bg-primary text-white dark:bg-primary dark:text-white'
                            : 'bg-secondary text-white dark:bg-secondary dark:text-white'
                        }
                       transition-transform cursor-pointer hover:opacity-90 active:opacity-80
                       overflow-x-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent
                       text-[10px] min-h-[20px]
                       ${event.hasBookings ? 'border-2 border-orange-400' : ''}
                    `}
                        title={`${event.title} ${event.isRecurring ? '(Récurrent)' : '(Ponctuel)'}${event.hasBookings ? ' - Réservé' : ''}`}
                    >
                      <div className="flex items-center whitespace-nowrap min-w-0 pr-2">
                        {event.isRecurring && (
                          <Repeat2 className="w-3 h-3 mr-1 opacity-80 flex-shrink-0" />
                        )}
                        <p className="overflow-x-auto no-scrollbar">{event.title}</p>
                        {event.hasBookings && (
                          <span className="ml-1 text-[8px] bg-orange-500 px-1 rounded flex-shrink-0">Réservé</span>
                        )}
                      </div>
                      <div className='hidden sm:flex items-center'>
                        <button
                            onClick={(e) => {
                              e.stopPropagation();
                              viewModel.setIsEditModalOpen(event);
                            }}
                            className="text-white hover:text-white-600 dark:text-white-400 dark:hover:text-white-300 mr-3"
                            aria-label={t("availability.editSlot", "Modifier le créneau") || "Modifier le créneau"}
                        >
                          <Edit className="w-4 h-4"/>
                        </button>
                        <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (event.hasBookings) {
                                alert(t('availability.cannotDeleteWithBookings', 'Impossible de supprimer un créneau avec des réservations'));
                                return;
                              }
                              viewModel.setIsConfirmDialogOpen(event.id);
                            }}
                            className={`${
                              event.hasBookings
                                ? 'text-gray-400 cursor-not-allowed opacity-50'
                                : 'text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300'
                            }`}
                            aria-label={t("availability.deleteSlot", "Supprimer le créneau") || "Supprimer le créneau"}
                            disabled={event.hasBookings}
                            title={event.hasBookings ? t('availability.cannotDeleteWithBookings', 'Impossible de supprimer un créneau avec des réservations') || '' : ''}
                        >
                          <Trash2 className="w-4 h-4"/>
                        </button>
                      </div>
                    </div>
                ))}
              </div>

              {/* Indicateur visuel pour les jours avec événements */}
              {dayEvents.length > 0 && !isCurrentMonth && (
                  <div className="absolute bottom-1 right-1 w-2 h-2 rounded-full bg-primary-500"></div>
              )}
            </div>
          );
        })}
      </div>

      {/* Dialogue de confirmation pour la suppression */}
      <ConfirmDialog
          isOpen={viewModel.isConfirmDialogOpen}
          onClose={() => viewModel.setIsConfirmDialogOpen()}
          onConfirm={() => viewModel.handleDeleteTimeSlot()}
          title={t("availability.confirmDelete", "Confirmer la suppression") || "Confirmer la suppression"}
          message={t("availability.confirmDeleteMessage", "Êtes-vous sûr de vouloir supprimer ce créneau ? Cette action est irréversible.") || "Êtes-vous sûr de vouloir supprimer ce créneau ? Cette action est irréversible."}
          confirmText={t("availability.delete", "Supprimer") || "Supprimer"}
          cancelText={t("availability.cancel", "Annuler") || "Annuler"}
          variant="danger"
      />

      {/* Modal d'édition de créneau */}
      <TimeSlotEditModal
          isOpen={viewModel.isEditModalOpen}
          onClose={() => viewModel.setIsEditModalOpen()}
          timeSlot={viewModel.selectedTimeSlot ? {
            id: viewModel.selectedTimeSlot.id,
            date: viewModel.editDate,
            startTime: viewModel.editStartTime,
            endTime: viewModel.editEndTime,
            isRecurring: viewModel.selectedTimeSlot.isRecurring,
            hasBookings: viewModel.selectedTimeSlot.hasBookings,
          dayOfWeek: viewModel.selectedTimeSlot.isRecurring ? dayNames[viewModel.selectedTimeSlot.dayOfWeek || 0] : undefined,
          serviceName: viewModel.selectedTimeSlot.title?.includes('-') ? viewModel.selectedTimeSlot.title.split('-')[0].trim() : undefined
        } : null}
      />
    </div>
  );
});

export default AvailabilityCalendar;
